
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { logAuthState } from "@/utils/debug-auth";

const RoleSelection = () => {
  const { user, loading: authLoading, authStateReady } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [selectedRole, setSelectedRole] = useState<"candidate" | "employer" | null>(null);
  
  // Get returnTo parameter from URL
  const returnTo = searchParams.get('returnTo');
  
  // Debug auth state on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      logAuthState();
      console.log("[RoleSelection] Component mounted with auth state:", { 
        user: !!user, 
        authLoading, 
        authStateReady,
        returnTo
      });
    }, 300);
    
    return () => clearTimeout(timer);
  }, [user, authLoading, authStateReady, returnTo]);
  
  // Check for intended role and pending invite context; restore from localStorage if needed
  useEffect(() => {
    const intendedRole = sessionStorage.getItem('intendedRole');
    console.log('[RoleSelection] Checking for intended role in session storage:', intendedRole);
    if (intendedRole === 'employer') {
      setSelectedRole('employer');
      sessionStorage.removeItem('intendedRole');
      console.log('[RoleSelection] Found employer role in session storage, setting selected role');
    }

    console.log('[RoleSelection] Return to parameter:', returnTo);

    // Pull from both sessionStorage and localStorage
    const ss = {
      testId: sessionStorage.getItem('pendingTestId'),
      token: sessionStorage.getItem('pendingTestInviteToken'),
      redirectFlag: sessionStorage.getItem('redirectToJobPage')
    } as const;
    const ls = {
      testId: localStorage.getItem('pendingTestId'),
      token: localStorage.getItem('pendingTestInviteToken'),
      redirectFlag: localStorage.getItem('redirectToJobPage')
    } as const;

    // Restore into sessionStorage if missing (common after OAuth)
    if (!ss.token && ls.token) sessionStorage.setItem('pendingTestInviteToken', ls.token);
    if (!ss.testId && ls.testId) sessionStorage.setItem('pendingTestId', ls.testId);
    if (!ss.redirectFlag && ls.redirectFlag) sessionStorage.setItem('redirectToJobPage', ls.redirectFlag);

    const pendingTestId = ss.testId || ls.testId;
    const pendingTestInviteToken = ss.token || ls.token;
    const shouldRedirectToJob = (ss.redirectFlag || ls.redirectFlag) === 'true';

    console.log('[RoleSelection] Pending (merged):', { pendingTestId, pendingTestInviteToken, shouldRedirectToJob });

    // If we have a pending test invite, default to candidate role
    if ((pendingTestId && shouldRedirectToJob) || pendingTestInviteToken) {
      setSelectedRole('candidate');
      console.log('[RoleSelection] Found pending test data, setting role to candidate');
    }
  }, [returnTo]);

  // Add a delay before checking auth state to allow for supabase auth to initialize
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false);
    }, 1500); // Give the auth state time to resolve
    
    return () => clearTimeout(timer);
  }, []);

  // Check if user already has a role and redirect if they do
  useEffect(() => {
    // Only proceed if we're done with initial loading
    if (isInitializing || authLoading || !authStateReady) {
      return;
    }
    
    console.log('[RoleSelection] Auth state check - user:', !!user, 'initializing:', isInitializing);
    
    if (!user) {
      // Check if we have a session in localStorage that hasn't been loaded yet
      const hasStoredSession = localStorage.getItem('testera_auth_token') !== null;
      console.log('[RoleSelection] No user in state, checking localStorage:', hasStoredSession);
      
      if (hasStoredSession) {
        // If we have a session in storage but no user in state, wait a bit longer
        console.log('[RoleSelection] Found session in localStorage but user not in state yet, waiting...');
        setTimeout(() => {
          // Re-check auth state after a delay
          supabase.auth.getSession().then(({ data: { session } }) => {
            if (!session) {
              console.log('[RoleSelection] No session found after delay, redirecting to login');
              navigate('/login', { 
                state: { returnTo: returnTo || '/' }
              });
            } else {
              console.log('[RoleSelection] Session found after delay:', session.user.id);
            }
          });
        }, 1000);
        return;
      }
      
      console.log('[RoleSelection] No user and no stored session, redirecting to login');
      navigate('/login', { 
        state: { returnTo: returnTo || '/dashboard' }
      });
      return;
    }

    // Check if user already has a role set in their metadata
    const userRole = user.user_metadata?.role;
    console.log('[RoleSelection] User role from metadata:', userRole);
    
    if (userRole) {
      console.log('[RoleSelection] User already has role, preparing to navigate');
      // If user already has a role, trigger navigation with a small delay
      // to ensure any profile updates have completed
      setTimeout(() => {
        navigateAfterRoleSelection();
      }, 500);
    }
  }, [user, navigate, isInitializing, authLoading, authStateReady]);

  const handleRoleSelection = async (role: "candidate" | "employer") => {
    if (!user) {
      console.log('[RoleSelection] Cannot set role, no user found');
      return;
    }
    
    setIsLoading(true);
    setSelectedRole(role);
    
    try {
      console.log('[RoleSelection] Setting role:', role);
      
      // Update user metadata with selected role
      const { error } = await supabase.auth.updateUser({
        data: { role }
      });
      
      if (error) throw error;
      
      // First check if profile exists
      const { data: existingProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is the error code for "no rows returned"
        throw fetchError;
      }

      // Update or insert profile based on whether it exists
      if (existingProfile) {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ 
            role,
            // Ensure these fields are set if they weren't already
            email: user.email,
            full_name: user.user_metadata?.full_name || user.user_metadata?.name || null,
            avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || null
          })
          .eq('id', user.id);
        
        if (updateError) throw updateError;
        console.log('[RoleSelection] Profile updated successfully');
      } else {
        // Insert new profile
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            role,
            email: user.email,
            full_name: user.user_metadata?.full_name || user.user_metadata?.name || null,
            avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || null
          });
            
        if (insertError) throw insertError;
        console.log('[RoleSelection] Profile created successfully');
      }
      
      // Invalidate profile query to refresh data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
      toast({
        title: "Role Selected",
        description: `You've selected the ${role} role.`,
        className: "bg-card text-foreground border-none",
      });
      
      console.log('[RoleSelection] Role selection successful, preparing to navigate');
      
      // Navigate based on context with a small delay to ensure auth state is stable
      setTimeout(() => {
        navigateAfterRoleSelection();
      }, 500);
    } catch (error: any) {
      console.error("[RoleSelection] Error setting role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to set role",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const navigateAfterRoleSelection = () => {
    console.log('[RoleSelection] Navigating after role selection');

    // Highest priority: explicit post-auth target persisted for OAuth roundtrip
    const postAuthReturnTo = localStorage.getItem('postAuthReturnTo');
    if (postAuthReturnTo) {
      console.log('[RoleSelection] Using postAuthReturnTo:', postAuthReturnTo);
      try { localStorage.removeItem('postAuthReturnTo'); } catch {}
      navigate(postAuthReturnTo, { replace: true });
      return;
    }
    
    // Merge context from session/local
    const pendingToken = sessionStorage.getItem('pendingTestInviteToken') || localStorage.getItem('pendingTestInviteToken');
    const testId = sessionStorage.getItem('pendingTestId') || localStorage.getItem('pendingTestId');
    const redirectFlag = (sessionStorage.getItem('redirectToJobPage') || localStorage.getItem('redirectToJobPage')) === 'true';

    // If we have a pending test invite token, redirect back to the invite handler
    if (pendingToken) {
      console.log('[RoleSelection] Redirecting to test invite handler:', pendingToken);
      navigate(`/test/invite/${pendingToken}`, { replace: true });
      return;
    }

    // Otherwise follow standard job/returnTo flow
    if (testId && redirectFlag) {
      console.log('[RoleSelection] Navigating to job page after role selection:', testId);
      // Clear context from both storages
      try {
        sessionStorage.removeItem('pendingTestInviteToken');
        sessionStorage.removeItem('pendingTestId');
        sessionStorage.removeItem('redirectToJobPage');
        localStorage.removeItem('pendingTestInviteToken');
        localStorage.removeItem('pendingTestId');
        localStorage.removeItem('redirectToJobPage');
      } catch {}
      navigate(`/job/${testId}`, { replace: true });
    } else if (returnTo && returnTo !== '/') {
      console.log('[RoleSelection] Navigating to:', returnTo);
      navigate(returnTo, { replace: true });
    } else {
      console.log('[RoleSelection] Navigating to dashboard after role selection');
      navigate('/dashboard', { replace: true });
    }
  };

  // Improved loading state that accounts for all auth states
  if (isInitializing || authLoading || !authStateReady) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <Loader2 className="h-10 w-10 animate-spin text-foreground mb-4" />
        <p className="text-muted-foreground">Checking authentication...</p>
      </div>
    );
  }

  // If no user, show a different loading state while potential redirect happens
  if (!user) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen">
        <Loader2 className="h-10 w-10 animate-spin text-foreground mb-4" />
        <p className="text-muted-foreground">Initializing session...</p>
        <p className="text-xs text-muted-foreground mt-2">If this takes too long, you'll be redirected to login.</p>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md p-8 space-y-6 bg-card border-border">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Select Your Role</h1>
          <p className="text-muted-foreground">
            Choose how you want to use Testera. You can be either a candidate looking for opportunities or an employer looking for talent.
          </p>
        </div>
        
        <div className="flex flex-col gap-4">
          <Button
            onClick={() => handleRoleSelection("candidate")}
            variant={selectedRole === "candidate" ? "default" : "outline"}
            className="w-full h-20 flex flex-col items-center justify-center"
            disabled={isLoading}
          >
            <span className="text-lg font-medium">I'm a Candidate</span>
            <span className="text-xs text-muted-foreground">Looking for opportunities</span>
          </Button>
          
          <Button
            onClick={() => handleRoleSelection("employer")}
            variant={selectedRole === "employer" ? "default" : "outline"}
            className="w-full h-20 flex flex-col items-center justify-center"
            disabled={isLoading}
          >
            <span className="text-lg font-medium">I'm an Employer</span>
            <span className="text-xs text-muted-foreground">Looking for talent</span>
          </Button>
        </div>
        
        {isLoading && (
          <div className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        
        <p className="text-xs text-center text-muted-foreground">
          You can change your role later in your account settings.
        </p>
      </Card>
    </div>
  );
};

export default RoleSelection;
